import React from 'react'
import { Button, Empty, Space, Spin, Table, Tag } from 'antd'
import {
    DeleteOutlined,
    DownloadOutlined,
    EyeOutlined,
    FileImageOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type {
    SortOrder,
    SorterResult,
    TablePaginationConfig,
} from 'antd/es/table/interface'
import { useTranslation } from 'react-i18next'

import {
    HistoryListItem,
    HistoryReviewStatus,
    HistoryTotalStatus,
} from '../../types/history'
import {
    getHistoryPaginationTotal,
    HistoryPaginationMeta,
    renderHistoryPaginationTotal,
} from '../../utils/historyPagination'
import { formatConfiguredDetectionClassSummary } from '../../utils/detectionClassDisplay'
import { formatRawClassSummary } from '../../utils/rawClassDisplay'
import { supportsHistoryTrends } from '../../utils/historyCapabilities'
import {
    DEVICE_PACKAGE_DEFINITIONS,
    isDevicePackageKey,
} from '../../constants/devicePackages'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../common/PaginatedListTable'
import { HistoryDeviceNameDisplay } from './HistoryDeviceNameDisplay'
import { UnsupportedCapabilityText } from './UnsupportedCapabilityText'

const actionColumnStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    alignItems: 'center',
    columnGap: 12,
    rowGap: 8,
    maxWidth: '100%',
}

const actionLinkButtonStyle: React.CSSProperties = {
    paddingInline: 4,
    height: 24,
    lineHeight: '24px',
    whiteSpace: 'normal',
}

const resultSummaryStackStyle: React.CSSProperties = {
    width: '100%',
}

const resultSummaryTagStyle: React.CSSProperties = {
    display: 'inline-block',
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
}

const SUMMARY_SEGMENT_PATTERN = /^(.+?)(\((\d+)\))$/

const getSummaryTotal = (summary: string | null | undefined): number => {
    if (!summary) {
        return 0
    }

    return summary.split(',').reduce((total, part) => {
        const segment = part.trim()
        if (!segment) {
            return total
        }

        const match = segment.match(SUMMARY_SEGMENT_PATTERN)
        const count = Number(match?.[3] ?? '1')
        return total + (Number.isFinite(count) ? count : 1)
    }, 0)
}

const HISTORY_LIST_TABLE_SCROLL_X = 1620

interface HistoryListProps {
    records: HistoryListItem[]
    total: number
    totalStatus: HistoryTotalStatus
    paginationMeta?: HistoryPaginationMeta
    currentPage: number
    pageSize: number
    loading: boolean
    editable: boolean
    showReviewSummary: boolean
    showReviewUpdatedAtColumn?: boolean
    bypassCapabilityFallback?: boolean
    showViewAction?: boolean
    showExportAction?: boolean
    showDeleteAction?: boolean
    onDelete?: (id: string) => void
    onView?: (record: HistoryListItem) => void
    onExport?: (record: HistoryListItem) => void
    onPageChange: (page: number, pageSize: number) => void
    /** @deprecated Deep page browsing is no longer blocked by this component. */
    onPageLimitExceeded?: () => void
    onSortChange: (sortOrder: 'asc' | 'desc') => void
    sortOrder: SortOrder
    formatDate: (timestamp: number) => string
    emptyDescription?: React.ReactNode
    rawClassLocalizedNames?: Record<string, string>
}

const getReviewStatus = (record: HistoryListItem): HistoryReviewStatus =>
    record.review_status || 'pending'

const getReviewTagColor = (status: HistoryReviewStatus): string => {
    switch (status) {
        case 'confirmed':
            return 'green'
        case 'missed_detection':
            return 'volcano'
        case 'incorrect_detection':
            return 'orange'
        case 'needs_review':
            return 'blue'
        default:
            return 'default'
    }
}

export const HistoryList: React.FC<HistoryListProps> = ({
    records,
    total,
    totalStatus,
    paginationMeta = {},
    currentPage,
    pageSize,
    loading,
    showReviewSummary,
    showReviewUpdatedAtColumn = false,
    bypassCapabilityFallback = false,
    showViewAction = true,
    showExportAction = false,
    showDeleteAction = false,
    onDelete,
    onView,
    onExport,
    onPageChange,
    onSortChange,
    sortOrder,
    formatDate,
    emptyDescription,
    rawClassLocalizedNames,
}) => {
    const { t } = useTranslation('history')
    const actionWidth =
        (showViewAction ? 120 : 0) +
        (showExportAction ? 90 : 0) +
        (showDeleteAction ? 90 : 0)
    const paginationTotal = getHistoryPaginationTotal(
        paginationMeta,
        pageSize,
        total,
    )
    const renderTotalText = () =>
        renderHistoryPaginationTotal(t, {
            total,
            totalStatus,
            pageSize,
            meta: paginationMeta,
        })
    const columns: ColumnsType<HistoryListItem> = [
        {
            title: t('table.fileName'),
            dataIndex: 'filename',
            key: 'filename',
            width: 260,
            ellipsis: true,
            render: (text) => (
                <Space style={{ maxWidth: '100%' }}>
                    <FileImageOutlined />
                    {renderPaginatedListText(text)}
                </Space>
            ),
        },
        {
            title: t('table.deviceName'),
            dataIndex: 'device_name',
            key: 'device_name',
            width: 220,
            ellipsis: true,
            render: (_, record) => (
                <HistoryDeviceNameDisplay
                    record={record}
                    fallback={t('messages.deviceUnavailable')}
                />
            ),
        },
        {
            title: t('table.packageAtUpload'),
            dataIndex: 'package_key_at_upload',
            key: 'package_key_at_upload',
            width: 180,
            render: (packageKey: string | null | undefined) => {
                if (!isDevicePackageKey(packageKey)) {
                    return '-'
                }

                const definition = DEVICE_PACKAGE_DEFINITIONS[packageKey]
                return (
                    <Tag color={definition.tagColor}>
                        {t(`device:${definition.labelI18nKey}`)}
                    </Tag>
                )
            },
        },
        {
            title: t('table.uploadTime'),
            key: 'timestamp',
            width: 220,
            render: (_, record) => formatDate(record.timestamp),
            sorter: !showReviewUpdatedAtColumn,
            sortDirections: ['descend', 'ascend', 'descend'],
            sortOrder: showReviewUpdatedAtColumn ? undefined : sortOrder,
        },
        ...(showReviewUpdatedAtColumn
            ? [
                  {
                      title: t('history:table.reviewUpdatedAt'),
                      key: 'review_updated_at',
                      width: 220,
                      render: (_: unknown, record: HistoryListItem) =>
                          formatDate(
                              record.review_updated_at ?? record.timestamp,
                          ),
                      sorter: true,
                      sortDirections: ['descend', 'ascend', 'descend'] as const,
                      sortOrder,
                  },
              ]
            : []),
        {
            title: t('table.result'),
            key: 'result',
            width: 260,
            render: (_, record) => {
                const unsupportedText = t('messages.currentVersionUnsupported')
                const supportsHistory =
                    bypassCapabilityFallback ||
                    supportsHistoryTrends(record.feature_keys_at_upload)
                const primarySummaryValue = showReviewSummary
                    ? record.system_summary
                    : record.effective_summary
                const systemSummaryTotal = getSummaryTotal(primarySummaryValue)
                const systemSummary = showReviewSummary
                    ? formatRawClassSummary(primarySummaryValue, t, {
                          withCode: true,
                          localizedNames: rawClassLocalizedNames,
                      })
                    : formatConfiguredDetectionClassSummary(
                          primarySummaryValue,
                          t,
                      )
                const reviewSummaryTotal = getSummaryTotal(
                    record.review_summary,
                )
                const reviewSummary = showReviewSummary
                    ? formatRawClassSummary(record.review_summary, t, {
                          withCode: true,
                          localizedNames: rawClassLocalizedNames,
                      })
                    : formatConfiguredDetectionClassSummary(
                          record.review_summary,
                          t,
                      )
                const shouldShowReviewTag =
                    showReviewSummary &&
                    (record.review_status !== 'pending' ||
                        Boolean(reviewSummary))

                return (
                    <Space
                        direction="vertical"
                        size={4}
                        style={resultSummaryStackStyle}
                    >
                        {supportsHistory ? (
                            <Tag
                                color={systemSummary ? 'green' : 'orange'}
                                style={resultSummaryTagStyle}
                            >
                                {showReviewSummary
                                    ? `${t('summary.system')}(${systemSummaryTotal}): `
                                    : null}
                                {systemSummary || t('messages.noDetection')}
                            </Tag>
                        ) : (
                            <UnsupportedCapabilityText>
                                {showReviewSummary
                                    ? `${t('summary.system')}: ${unsupportedText}`
                                    : unsupportedText}
                            </UnsupportedCapabilityText>
                        )}
                        {supportsHistory && shouldShowReviewTag && (
                            <Tag
                                color={reviewSummary ? 'blue' : 'default'}
                                style={resultSummaryTagStyle}
                            >
                                {t('summary.review')}({reviewSummaryTotal}):{' '}
                                {reviewSummary || t('messages.noDetection')}
                            </Tag>
                        )}
                    </Space>
                )
            },
        },
        ...(showReviewSummary
            ? [
                  {
                      title: t('table.reviewStatus'),
                      key: 'review_status',
                      width: 180,
                      render: (_: unknown, record: HistoryListItem) => {
                          const reviewStatus = getReviewStatus(record)
                          return (
                              <Tag color={getReviewTagColor(reviewStatus)}>
                                  {t(`review.status.${reviewStatus}`)}
                              </Tag>
                          )
                      },
                  },
              ]
            : []),
        {
            title: t('table.actions'),
            key: 'action',
            width: Math.max(actionWidth, 90),
            fixed: 'right' as const,
            render: (_, record) => (
                <div style={actionColumnStyle}>
                    {showViewAction && onView && (
                        <Button
                            icon={<EyeOutlined />}
                            type="link"
                            size="small"
                            onClick={() => onView(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('actions.viewDetails')}
                        </Button>
                    )}
                    {showExportAction && onExport && (
                        <Button
                            icon={<DownloadOutlined />}
                            type="link"
                            size="small"
                            onClick={() => onExport(record)}
                            style={actionLinkButtonStyle}
                        >
                            {t('actions.exportDataset')}
                        </Button>
                    )}
                    {showDeleteAction && onDelete && (
                        <Button
                            icon={<DeleteOutlined />}
                            type="link"
                            danger
                            size="small"
                            onClick={() => onDelete(record.id)}
                            style={actionLinkButtonStyle}
                        >
                            {t('actions.delete')}
                        </Button>
                    )}
                </div>
            ),
        },
    ]

    if (loading) {
        return (
            <PaginatedListCard>
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                </div>
            </PaginatedListCard>
        )
    }

    if (records.length === 0) {
        return (
            <PaginatedListCard>
                <Empty
                    description={emptyDescription || t('messages.noRecords')}
                />
            </PaginatedListCard>
        )
    }

    return (
        <PaginatedListCard>
            <Table
                columns={columns}
                dataSource={records}
                rowKey="id"
                size={PAGINATED_LIST_TABLE_SIZE}
                tableLayout="fixed"
                scroll={{ x: HISTORY_LIST_TABLE_SCROLL_X }}
                onChange={(
                    pagination: TablePaginationConfig,
                    _filters,
                    sorter:
                        | SorterResult<HistoryListItem>
                        | SorterResult<HistoryListItem>[],
                ) => {
                    const resolvedPage = pagination.current ?? currentPage
                    const resolvedPageSize = pagination.pageSize ?? pageSize
                    if (
                        resolvedPage !== currentPage ||
                        resolvedPageSize !== pageSize
                    ) {
                        const nextPage =
                            resolvedPageSize !== pageSize ? 1 : resolvedPage
                        onPageChange(nextPage, resolvedPageSize)
                    }

                    const resolvedSorter = Array.isArray(sorter)
                        ? sorter[0]
                        : sorter
                    const sortableColumnKey = showReviewUpdatedAtColumn
                        ? 'review_updated_at'
                        : 'timestamp'
                    if (
                        resolvedSorter?.columnKey === sortableColumnKey &&
                        resolvedSorter.order
                    ) {
                        const nextSortOrder =
                            resolvedSorter.order === 'ascend' ? 'asc' : 'desc'
                        const currentSortOrder =
                            sortOrder === 'ascend' ? 'asc' : 'desc'
                        if (nextSortOrder !== currentSortOrder) {
                            onSortChange(nextSortOrder)
                        }
                    }
                }}
                pagination={buildPaginatedListPagination({
                    current: currentPage,
                    pageSize,
                    total: paginationTotal,
                    showTotal: renderTotalText,
                })}
            />
        </PaginatedListCard>
    )
}
